import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, TrendingDown, Info, CheckCircle, ArrowRight, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CostComparisonPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authHeaders, user } = useAuth();

  const [procedures, setProcedures] = useState([]);
  const [selectedProc, setSelectedProc] = useState(searchParams.get('cpt') || '');
  const [deductible, setDeductible] = useState(5000);
  const [oopMax, setOopMax] = useState(8000);
  const [alreadyPaid, setAlreadyPaid] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const providerId = searchParams.get('provider');

  useEffect(() => {
    axios.get(`${API}/procedures`).then(res => setProcedures(res.data));
  }, []);

  useEffect(() => {
    if (selectedProc) handleCompare();
  }, [selectedProc, providerId]);

  const handleCompare = async () => {
    // Find proc by cpt or id
    const proc = procedures.find(p => p.cpt === selectedProc || p.id === selectedProc);
    if (!proc && !selectedProc) return;
    setLoading(true);
    try {
      const params = {
        procedure_id: proc?.id || procedures.find(p => p.cpt === selectedProc)?.id || selectedProc,
        deductible,
        oop_max: oopMax,
        already_paid: alreadyPaid,
      };
      if (providerId) params.provider_id = providerId;
      const res = await axios.get(`${API}/cost-comparison`, { params });
      setResult(res.data);
    } catch (e) {
      console.error('Comparison failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!result) return;
    try {
      await axios.post(`${API}/user/saved-searches`, {
        procedure_name: result.procedure?.name,
        procedure_cpt: result.procedure?.cpt,
        insurance_deductible: deductible,
        insurance_oop_max: oopMax,
        already_paid: alreadyPaid,
      }, { headers: authHeaders });
      setSaved(true);
    } catch (e) {
      console.error('Save failed', e);
    }
  };

  const procId = procedures.find(p => p.cpt === selectedProc)?.id || selectedProc;

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Cost Comparison</p>
          <h1 className="text-4xl font-medium text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Cash vs. Insurance Calculator</h1>
          <p className="text-stone-600 max-w-2xl">Enter your insurance details to see exactly how much you'd pay out-of-pocket versus paying the cash rate.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Calculator Inputs */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-stone-200 p-8">
              <h2 className="text-lg font-semibold text-stone-900 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Your Insurance Details</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Select Procedure</label>
                  <select
                    value={selectedProc}
                    onChange={e => setSelectedProc(e.target.value)}
                    className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
                    data-testid="procedure-select"
                  >
                    <option value="">-- Select a procedure --</option>
                    {procedures.map(p => (
                      <option key={p.id} value={p.cpt}>{p.name} (CPT {p.cpt})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Annual Deductible: <span className="text-[#0D3D4A]">${deductible.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={15000}
                    step={500}
                    value={deductible}
                    onChange={e => setDeductible(Number(e.target.value))}
                    className="w-full accent-[#0D3D4A]"
                    data-testid="deductible-slider"
                  />
                  <div className="flex justify-between text-xs text-[#A8B4B8] mt-1"><span>$0</span><span>$15,000</span></div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Out-of-Pocket Maximum: <span className="text-[#0D3D4A]">${oopMax.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={20000}
                    step={500}
                    value={oopMax}
                    onChange={e => setOopMax(Number(e.target.value))}
                    className="w-full accent-[#0D3D4A]"
                    data-testid="oop-max-slider"
                  />
                  <div className="flex justify-between text-xs text-[#A8B4B8] mt-1"><span>$0</span><span>$20,000</span></div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Already Applied Toward Deductible: <span className="text-[#0D3D4A]">${alreadyPaid.toLocaleString()}</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={deductible}
                    step={100}
                    value={alreadyPaid}
                    onChange={e => setAlreadyPaid(Number(e.target.value))}
                    className="w-full accent-[#0D3D4A]"
                    data-testid="already-paid-slider"
                  />
                  <div className="flex justify-between text-xs text-[#A8B4B8] mt-1"><span>$0</span><span>${deductible.toLocaleString()}</span></div>
                </div>

                <button
                  onClick={handleCompare}
                  disabled={!selectedProc || loading}
                  className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-50"
                  data-testid="compare-btn"
                >
                  {loading ? 'Calculating...' : 'Calculate Savings'}
                </button>
              </div>
            </div>

            <div className="bg-[#F0EEE9] border border-stone-200 p-6">
              <div className="flex items-start gap-3">
                <Info size={16} className="text-[#A8B4B8] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-stone-500 leading-relaxed">
                  Insurance out-of-pocket estimates are based on typical cost-sharing for in-network providers. Actual costs may vary based on your specific plan, provider network status, and whether your deductible has been met.
                </p>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {!result ? (
              <div className="bg-white border border-stone-200 p-16 text-center h-full flex flex-col items-center justify-center">
                <DollarSign size={48} className="text-stone-200 mb-4" />
                <p className="text-[#A8B4B8] text-lg font-medium mb-2">Select a procedure to see your savings</p>
                <p className="text-[#A8B4B8] text-sm">Enter your insurance details and click Calculate Savings.</p>
              </div>
            ) : (
              <div className="space-y-6 animate-fade-in">
                {/* Headline Result */}
                <div className={`p-8 border-2 ${result.is_cash_better ? 'bg-[#F0EEE9] border-[#0D3D4A]' : 'bg-[#F0EEE9] border-stone-200'}`} data-testid="comparison-result">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-2">Analysis Complete</p>
                      <h2 className="text-2xl font-bold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>{result.procedure?.name}</h2>
                      <p className="text-sm text-stone-500 mt-1">CPT {result.procedure?.cpt}</p>
                    </div>
                    {result.is_cash_better && (
                      <div className="bg-[#0D3D4A] text-white px-4 py-2 text-center">
                        <p className="text-xs font-semibold uppercase tracking-wide">Cash Wins</p>
                        <p className="text-xl font-bold">{result.savings_percentage}% savings</p>
                      </div>
                    )}
                  </div>

                  {/* Comparison Bars */}
                  <div className="space-y-4 mb-8">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-stone-700">Insurance Out-of-Pocket Est.</span>
                        <span className="font-bold text-stone-500">${result.insurance_out_of_pocket?.toLocaleString()}</span>
                      </div>
                      <div className="h-10 bg-stone-200 w-full relative">
                        <div className="h-full bg-stone-400 flex items-center justify-end pr-3" style={{width: '100%'}}>
                          <span className="text-white text-xs font-semibold">Insurance</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-stone-700">Cash Pay Price</span>
                        <span className="font-bold text-[#0D3D4A] text-lg">${result.cash_price?.toLocaleString()}</span>
                      </div>
                      <div className="h-10 bg-stone-100 w-full relative">
                        <div
                          className="h-full bg-[#0D3D4A] flex items-center justify-end pr-3 transition-all duration-700"
                          style={{width: `${Math.min(100, (result.cash_price / result.insurance_out_of_pocket) * 100)}%`}}
                        >
                          <span className="text-white text-xs font-semibold">Cash</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Savings Callout */}
                  {result.is_cash_better && (
                    <div className="bg-orange-50 border border-orange-200 p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-orange-800">Estimated Savings</p>
                        <p className="text-xs text-orange-600">By choosing cash pay over insurance</p>
                      </div>
                      <p className="text-3xl font-bold text-orange-600" data-testid="savings-amount">${result.savings?.toLocaleString()}</p>
                    </div>
                  )}
                </div>

                {/* Breakdown */}
                <div className="bg-white border border-stone-200 p-8">
                  <h3 className="font-semibold text-stone-900 mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Cost Breakdown</h3>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-stone-100">
                      <tr className="py-3">
                        <td className="py-3 text-stone-600">Your annual deductible</td>
                        <td className="py-3 text-right font-medium">${result.inputs?.deductible?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-stone-600">Amount already applied</td>
                        <td className="py-3 text-right font-medium">${result.inputs?.already_paid?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-stone-600">Remaining deductible</td>
                        <td className="py-3 text-right font-medium">${result.remaining_deductible?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-stone-600">Out-of-pocket maximum</td>
                        <td className="py-3 text-right font-medium">${result.inputs?.oop_max?.toLocaleString()}</td>
                      </tr>
                      <tr className="font-semibold">
                        <td className="py-3 text-stone-900">Insurance OOP Estimate</td>
                        <td className="py-3 text-right text-stone-500">${result.insurance_out_of_pocket?.toLocaleString()}</td>
                      </tr>
                      <tr className="font-bold">
                        <td className="py-3 text-[#0D3D4A]">Cash Pay Price</td>
                        <td className="py-3 text-right text-[#0D3D4A] text-lg">${result.cash_price?.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saved}
                    className="flex-1 border border-[#0D3D4A] text-[#0D3D4A] py-3 text-sm font-medium hover:bg-[#F0EEE9] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="save-search-btn"
                  >
                    {saved ? <><CheckCircle size={16} /> Saved!</> : <><Save size={16} /> Save This Search</>}
                  </button>
                  <button
                    onClick={() => navigate('/providers?cpt=' + result.procedure?.cpt)}
                    className="flex-1 bg-[#0D3D4A] text-white py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2"
                    data-testid="find-providers-btn"
                  >
                    Find Providers <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
